import * as React from 'react';
import { Badge, Tooltip } from 'antd';
import { resolve } from 'inversify-react';
import { observer } from 'mobx-react';

import { DataLocalService } from 'src/core/services/data-local.service';
import { ToolbarVisibility, DataTableView, Column } from 'src/core/ui/data-table.view';
import { SearchResultItem } from 'src/app/bookings/search/results/search-result-item';
import { FormatService } from 'src/core/services/format.service';

interface Props {
	items: FlightSearchResult[];
	setBusy: (isBusy: boolean) => void;
	selection: { [unitIndex: string]: ResultSelection };
	onChangeSelection: (selectedRowKeys: string[] | number[], selectedRows: Object[]) => any;
	onChangeOptionalSelection: (selectedRowKeys: string[] | number[], selectedRows: Object[]) => any;
}

interface State { }

@observer
export class FlightCard extends React.Component<Props, State> {
	@resolve
	private formatService: FormatService;

	public render() {
		const { items, setBusy, selection } = this.props;

		const [item] = items;
		let minPrice: number;
		let minPriceCurrency: string;
		let minPriceProduct: string;
		for (const unit of Object.getOwnPropertyNames(item.sellPrice)) {
			const unitIndex = parseInt(unit);
			const price = item.sellPrice[unitIndex];
			if (minPrice === undefined || minPrice > price.totalPrice) {
				minPrice = price.totalPrice;
				minPriceProduct = `${item.product.name}`;
				minPriceCurrency = price.currencyIsoCode;
			}
		}
		const extra = (
			<Badge count={items.length > 1 ? items.length : 0} style={{ right: '-15px', marginTop: '5px' }}>
				<Tooltip title={minPriceProduct}>
					{minPrice} {minPriceCurrency}
				</Tooltip>
			</Badge>
		);

		const selectionKeys = [];
		const result: ResultOption[] = [];
		for (const prop of Object.getOwnPropertyNames(item.costPrice)) {
			const unitIndex = parseInt(prop);
			const cost = item.costPrice[unitIndex];
			const sell = item.sellPrice[unitIndex];
			result.push({
				id: item.id,
				unitIndex: unitIndex,
				cost: cost,
				sell: sell,
				result: item
			});
		}
		if (selection != null) {
			for (const prop of Object.getOwnPropertyNames(selection)) {
				const unitIndex = parseInt(prop);
				const id = selection[unitIndex].selected.id;
				if ([item].some(p => p.id === id)) {
					selectionKeys.push(`${id}`);
				}
			}
		}
		const model: any = new DataLocalService(result as any);
		const columns: Column[] = [
			{
				key: 'source',
				dataIndex: 'productProviderName',
				title: 'Source',
				render: (text, record: ResultOption) => {

					return record.result.productProviderName;
				}
			},
			{
				key: 'legs',
				dataIndex: 'legs',
				title: 'Legs',
				render: (text: any, record: ResultOption) => {

					const columns: Column[] = [
						{
							key: 'startDate',
							dataIndex: ' ',
							title: 'Departure Date',
							render: (text: any, record: FlightLegResult) => {
								return this.formatService.toString('long-date-time', record.startDate, 'utc');
							}
						},
						{
							key: 'DepartureAirport',
							dataIndex: '',
							title: 'Departure Airport',
							render: (text: any, record: FlightLegResult) => {
								return record.departureAirport ? record.departureAirport.code : '';
							}
						},
						{
							key: 'ArrivalAirport',
							dataIndex: '',
							title: 'Arrival Airport',
							render: (text: any, record: FlightLegResult) => {
								return record.destinationAirport ? record.destinationAirport.code : '';
							}
						},
						{
							key: 'endDate',
							dataIndex: ' ',
							title: 'Arrival Date',
							render: (text: any, record: FlightLegResult) => {
								return this.formatService.toString('long-date-time', record.endDate, 'utc');
							}
						},
					]

					const dataSet = new DataLocalService((record.result as FlightSearchResult).legs as any);
					return (
						<React.Fragment>
							<DataTableView
								rowKey={(record: FlightSearchResult) => record.id}
								mode="display"
								expandedRowRender={this.expandedRenderer}
								setBusy={this.props.setBusy}
								hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
								columns={columns}
								model={dataSet}
								selection={
									false
								}
							/>
						</React.Fragment>
					);
				}
			}
		];
		
		return (
			<SearchResultItem
				title={item.product.name}
				product={item.product}
				extra={extra}
			>
				<DataTableView
					rowKey={'id'}
					mode="display"
					setBusy={setBusy}
					hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
					columns={columns}
					model={model}
					selection={
						{
							selectedRowKeys: selectionKeys,
							onChange: this.props.onChangeSelection,
						}
					}
				/>
				{this.props.children}
			</SearchResultItem>
		);

	}
	private expandedRenderer = (leg: FlightLegResult) => {

		const cols = [
			{
				key: 'flightNumber',
				dataIndex: 'flightNumber',
				title: 'Flight Number'
			},
			{
				key: 'airline',
				dataIndex: 'airline',
				title: 'Airline'
			},
			{
				key: 'AirportDep',
				dataIndex: '',
				title: 'Airport Departure',
				render: (text: any, record: FlightSegmentResult) => {
					return record.departureAirport ? record.departureAirport.code : ''

				}
			},
			{
				key: 'AirportDepDate',
				dataIndex: ' ',
				title: 'Departure Time',
				render: (text: any, record: FlightSegmentResult) => {
					return this.formatService.toString('small-time', record.departureDateTime, 'utc');
				}
			},
			{
				key: 'AirportDes',
				dataIndex: '',
				title: 'Airport Destination',
				render: (text: any, record: FlightSegmentResult) => {
					return record.destinationAirport ? record.destinationAirport.code : ''

				}
			},
			{
				key: 'AirportDesDate',
				dataIndex: ' ',
				title: 'Arrival Time',
				render: (text: any, record: FlightSegmentResult) => {
					return this.formatService.toString('small-time', record.destinationDateTime, 'utc');
				}
			},
			{
				key: 'durationInMinutes',
				dataIndex: 'durationInMinutes',
				title: 'Duration In Minutes'
			},
		];
		const dataSet = new DataLocalService(leg.segments as any);
		return (
			<DataTableView
				columns={cols}
				model={dataSet}
				rowKey={'id'}
				mode="display"
				setBusy={this.props.setBusy}
				hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
				selection={false}
			/>
		);
	}

}



// WEBPACK FOOTER //
// ./src/app/bookings/search/results/search-results-flight.view.tsx